import React, { useState, useEffect, useCallback } from 'react';
import { facilitiesAPI, sportsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Edit, Plus, Trash2, Sun, Moon, Thermometer, Lightbulb, Ruler, Users, Euro, Clock, CalendarDays,
} from 'lucide-react';

const SURFACE_LABELS = {
  erba_sintetica: 'Erba Sintetica',
  erba_naturale: 'Erba Naturale',
  parquet: 'Parquet',
  cemento: 'Cemento',
  terra_battuta: 'Terra Battuta',
  tartan: 'Tartan',
  sabbia: 'Sabbia',
  gomma: 'Gomma',
  altro: 'Altro',
};

const SURFACE_TYPES = Object.keys(SURFACE_LABELS);

const EMPTY_FIELD = {
  name: '', sport_ids: [], surface_type: 'erba_sintetica',
  indoor: false, heated: false, lighted: true,
  max_players: '', hourly_rate: '', slot_duration_minutes: 60,
  dimensions: { length: '', width: '', unit: 'm' },
};

/** One field: players get "Prenota", managers get edit and delete. */
export const FieldCard = ({ field, onBook, onEdit, onDelete }) => (
  <Card className="relative overflow-hidden">
    {field.status === 'maintenance' && (
      <div className="absolute top-0 left-0 right-0 bg-yellow-500 text-white text-xs text-center py-1 font-medium">
        In manutenzione
      </div>
    )}
    <CardContent className={`p-5 ${field.status === 'maintenance' ? 'pt-8' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg">{field.name}</h3>
          <div className="flex flex-wrap gap-1 mt-2">
            {(field.sport_names || []).map((name, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{name}</Badge>
            ))}
          </div>
        </div>
        {onEdit && (
          <div className="flex gap-1 ml-2 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(field)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(field)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <Separator className="my-3" />

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Superficie:</span>
          <span className="font-medium">{SURFACE_LABELS[field.surface_type] || field.surface_type}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {field.indoor
            ? <><Moon className="h-3.5 w-3.5 text-muted-foreground" /><span>Indoor</span></>
            : <><Sun className="h-3.5 w-3.5 text-muted-foreground" /><span>Outdoor</span></>}
        </div>
        {field.heated && (
          <div className="flex items-center gap-1.5">
            <Thermometer className="h-3.5 w-3.5 text-orange-500" />
            <span>Riscaldato</span>
          </div>
        )}
        {field.lighted && (
          <div className="flex items-center gap-1.5">
            <Lightbulb className="h-3.5 w-3.5 text-yellow-500" />
            <span>Illuminato</span>
          </div>
        )}
        {field.max_players && (
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Max {field.max_players} giocatori</span>
          </div>
        )}
        {field.hourly_rate && (
          <div className="flex items-center gap-1.5">
            <Euro className="h-3.5 w-3.5 text-muted-foreground" />
            <span>€{field.hourly_rate}/h</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Slot da {field.slot_duration_minutes || 60} min</span>
        </div>
        {field.dimensions?.length && field.dimensions?.width && (
          <div className="flex items-center gap-1.5 col-span-2">
            <Ruler className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{field.dimensions.length}×{field.dimensions.width} {field.dimensions.unit || 'm'}</span>
          </div>
        )}
      </div>

      {onBook && (
        <Button
          size="sm"
          className="w-full mt-4 bg-gradient-to-br from-emerald-500 to-blue-600 hover:opacity-90"
          disabled={field.status === 'maintenance'}
          onClick={() => onBook(field)}
        >
          <CalendarDays className="h-4 w-4 mr-2" />
          Prenota
        </Button>
      )}
    </CardContent>
  </Card>
);

/** Management tab: list, add, edit and delete the fields of a facility. */
export const FacilityFieldsManager = ({ facilityId, onChange }) => {
  const [fields, setFields] = useState(null);
  const [allSports, setAllSports] = useState([]);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldForm, setFieldForm] = useState(EMPTY_FIELD);
  const [savingField, setSavingField] = useState(false);
  const [deleteField, setDeleteField] = useState(null);

  const fetchFields = useCallback(async () => {
    const res = await facilitiesAPI.getById(facilityId);
    setFields(res.data.fields || []);
  }, [facilityId]);

  useEffect(() => {
    fetchFields().catch(() => setFields([]));
    sportsAPI.getAll().then((res) => setAllSports(res.data)).catch(() => {});
  }, [fetchFields]);

  const refresh = () => {
    fetchFields();
    onChange?.();
  };

  const openNewFieldDialog = () => {
    setEditingField(null);
    setFieldForm(EMPTY_FIELD);
    setShowFieldDialog(true);
  };

  const openEditFieldDialog = (field) => {
    setEditingField(field);
    setFieldForm({
      name: field.name,
      sport_ids: field.sport_ids || [],
      surface_type: field.surface_type || 'altro',
      indoor: field.indoor || false,
      heated: field.heated || false,
      lighted: field.lighted !== false,
      max_players: field.max_players || '',
      hourly_rate: field.hourly_rate || '',
      slot_duration_minutes: field.slot_duration_minutes || 60,
      dimensions: field.dimensions || { length: '', width: '', unit: 'm' },
    });
    setShowFieldDialog(true);
  };

  const handleSaveField = async () => {
    if (!fieldForm.name.trim()) {
      toast.error('Inserisci il nome del campo');
      return;
    }
    if (fieldForm.sport_ids.length === 0) {
      toast.error('Seleziona almeno uno sport');
      return;
    }

    setSavingField(true);
    try {
      const payload = {
        ...fieldForm,
        max_players: fieldForm.max_players ? parseInt(fieldForm.max_players) : null,
        hourly_rate: fieldForm.hourly_rate ? parseFloat(fieldForm.hourly_rate) : null,
        dimensions: (fieldForm.dimensions.length && fieldForm.dimensions.width)
          ? fieldForm.dimensions : null,
      };

      if (editingField) {
        await facilitiesAPI.updateField(facilityId, editingField.field_id, payload);
        toast.success('Campo aggiornato');
      } else {
        await facilitiesAPI.createField(facilityId, payload);
        toast.success('Campo aggiunto');
      }
      setShowFieldDialog(false);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingField(false);
    }
  };

  const handleDeleteField = async () => {
    try {
      await facilitiesAPI.deleteField(facilityId, deleteField.field_id);
      toast.success('Campo eliminato');
      setDeleteField(null);
      refresh();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const toggleSport = (sportId) => {
    setFieldForm(prev => ({
      ...prev,
      sport_ids: prev.sport_ids.includes(sportId)
        ? prev.sport_ids.filter(s => s !== sportId)
        : [...prev.sport_ids, sportId],
    }));
  };

  if (!fields) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {fields.length === 1 ? '1 campo' : `${fields.length} campi`} · i giocatori prenotano negli orari liberi
        </p>
        <Button onClick={openNewFieldDialog} data-testid="add-field-btn">
          <Plus className="h-4 w-4 mr-2" />
          Aggiungi campo
        </Button>
      </div>

      {fields.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Ruler className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">Nessun campo registrato</p>
            <p className="text-sm text-muted-foreground mt-1">Aggiungi il primo campo per ricevere prenotazioni</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(field => (
            <FieldCard key={field.field_id} field={field} onEdit={openEditFieldDialog} onDelete={setDeleteField} />
          ))}
        </div>
      )}

      {/* ── Field Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Modifica Campo' : 'Nuovo Campo'}</DialogTitle>
            <DialogDescription>
              {editingField ? 'Modifica le informazioni del campo' : 'Aggiungi un nuovo campo alla struttura'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Name */}
            <div className="space-y-2">
              <Label>Nome Campo *</Label>
              <Input
                placeholder="Es: Campo 1 - Calcetto"
                value={fieldForm.name}
                onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value })}
                data-testid="field-name-input"
              />
            </div>

            {/* Sports multi-select */}
            <div className="space-y-2">
              <Label>Sport *</Label>
              <div className="flex flex-wrap gap-2">
                {allSports.map(sport => {
                  const selected = fieldForm.sport_ids.includes(sport.sport_id);
                  return (
                    <Badge
                      key={sport.sport_id}
                      variant={selected ? 'default' : 'outline'}
                      className="cursor-pointer select-none"
                      onClick={() => toggleSport(sport.sport_id)}
                    >
                      {sport.name}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Surface type */}
            <div className="space-y-2">
              <Label>Superficie</Label>
              <Select
                value={fieldForm.surface_type}
                onValueChange={(v) => setFieldForm({ ...fieldForm, surface_type: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SURFACE_TYPES.map(s => (
                    <SelectItem key={s} value={s}>{SURFACE_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                <Label className="text-xs">Indoor</Label>
                <Switch
                  checked={fieldForm.indoor}
                  onCheckedChange={(v) => setFieldForm({ ...fieldForm, indoor: v })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                <Label className="text-xs">Riscaldato</Label>
                <Switch
                  checked={fieldForm.heated}
                  onCheckedChange={(v) => setFieldForm({ ...fieldForm, heated: v })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                <Label className="text-xs">Illuminato</Label>
                <Switch
                  checked={fieldForm.lighted}
                  onCheckedChange={(v) => setFieldForm({ ...fieldForm, lighted: v })}
                />
              </div>
            </div>

            {/* Max players + hourly rate */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Giocatori</Label>
                <Input
                  type="number"
                  placeholder="Es: 10"
                  value={fieldForm.max_players}
                  onChange={(e) => setFieldForm({ ...fieldForm, max_players: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tariffa Oraria (€)</Label>
                <Input
                  type="number"
                  step="0.5"
                  placeholder="Es: 60"
                  value={fieldForm.hourly_rate}
                  onChange={(e) => setFieldForm({ ...fieldForm, hourly_rate: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Durata prenotazione</Label>
              <div className="grid grid-cols-3 gap-2">
                {[60, 90, 120].map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    onClick={() => setFieldForm({ ...fieldForm, slot_duration_minutes: minutes })}
                    className={`p-2 rounded-lg border text-sm transition-colors ${
                      fieldForm.slot_duration_minutes === minutes
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    {minutes === 60 ? '1 ora' : minutes === 90 ? '1 ora e ½' : '2 ore'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">I giocatori prenotano a blocchi di questa durata</p>
            </div>

            {/* Dimensions */}
            <div className="space-y-2">
              <Label>Dimensioni</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  placeholder="Lunghezza"
                  value={fieldForm.dimensions.length}
                  onChange={(e) => setFieldForm({
                    ...fieldForm,
                    dimensions: { ...fieldForm.dimensions, length: e.target.value },
                  })}
                  className="flex-1"
                />
                <span className="text-muted-foreground">×</span>
                <Input
                  type="number"
                  placeholder="Larghezza"
                  value={fieldForm.dimensions.width}
                  onChange={(e) => setFieldForm({
                    ...fieldForm,
                    dimensions: { ...fieldForm.dimensions, width: e.target.value },
                  })}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground w-6">m</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveField} disabled={savingField} data-testid="save-field-btn">
              {savingField ? 'Salvataggio...' : (editingField ? 'Salva' : 'Aggiungi Campo')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Field Confirm ──────────────────────────────────────────── */}
      <Dialog open={!!deleteField} onOpenChange={(o) => !o && setDeleteField(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Elimina Campo</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare {deleteField?.name}? Le prenotazioni future verranno cancellate.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteField(null)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDeleteField}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
